import React, { useEffect, useState, useContext, useRef } from 'react';
import { FuelBottomSheet } from '@frete.com/fuel-station-react';
import { FuelTypography } from '@frete.com/fuel-react';
import AppContext from '@context';
import { finishJourney } from '@services/journeys';
import useAmplitude from '@hooks/useAmplitude';
import useLoadJourneys from '@hooks/useLoadJourneys';
import { getJourney } from '@helpers/journeys/selectorState';
import { IEvaluatingStatus, JourneysItemInterface } from '@interface/journeys';
import { openCompanyEvaluating } from '@utils/companyEvaluating/companyEvaluating';
import JourneyOnboarding from '@components/application/JourneyOnboarding';
import { useParams } from 'react-router-dom';
import { JourneyListProps } from './JourneyListType';
import Dialog from '../Dialog/Dialog';
import * as Styled from './JourneyListStyles';
import JourneysAllTab from './JourneysAllTab';
import { ActionClickMarkerType } from './JourneysAllTab/JourneysAllTabContainerTypes';

function JourneyList({
  onErrorGetAgain,
  handlerOpenModalConfirm,
  handlercloseModalConfirm,
  isMobile,
}: JourneyListProps) {
  const { amplitudeEvent } = useAmplitude();
  const [evaluatingStatus, setEvaluatingStatus] = useState<IEvaluatingStatus>();
  const {
    state: { filters, journeys, proposals, ui },
    dispatch,
  } = useContext(AppContext);
  const { monitoringJourneyId } = useParams();
  const { isShow: isShowModalMonitoring } = ui.modalMonitoring;
  const { changePage, fetchJourneys } = useLoadJourneys('lazy');
  const {
    isShow: isShowDialogDeliveryConfirmation,
    journeyId: journeyIdDialogDeliveryConfirmation,
  } = ui.DialogDeliveryConfirmation;
  const [isLoading, setIsLoading] = useState(false);
  const errorCountRef = useRef(0);
  const journeySelected = journeys.all;

  const submitJourneyFinish = (id: string, amplitudeSource: string) => {
    setIsLoading(true);

    finishJourney(id)
      .then((response) => {
        amplitudeEvent('journey_finish_confirmed', {
          source: amplitudeSource,
          journey_id: id,
        });

        // Show toast when finished journey in monitoring modal
        // and show modal success in other flows
        if (isShowModalMonitoring) {
          dispatch({
            type: 'ui_modalMonitoring_set_show',
            payload: {
              isShow: false,
              journeyId: '',
            },
          });
        }
        window.dispatchEvent(
          new CustomEvent('notificationShow', {
            detail: {
              heading: 'Entrega confirmada com sucesso!',
              severity: 'success',
              journeyId: id,
              flow: 'finished',
              notificationId: '',
            },
          }),
        );
        handlercloseModalConfirm();
        handleJourneys();
        handleOpenEvaluating(id);

        setEvaluatingStatus(response);
      })
      .catch(() => {
        amplitudeEvent('journey_finish_failed', {
          source: amplitudeSource,
          journey_id: id,
        });

        window.dispatchEvent(
          new CustomEvent('notificationShow', {
            detail: {
              heading: 'Não foi possível confirmar a entrega',
              message: 'Tente novamente mais tarde.',
              severity: 'error',
              journeyId: id,
              flow: 'finished',
              notificationId: '',
            },
          }),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function handlePageChange(newPage: number) {
    let typeChange: 'anterior' | 'próximo' = 'anterior';

    if (journeySelected?.currentPage && journeySelected.currentPage < newPage) {
      typeChange = 'próximo';
    }

    amplitudeEvent('journey_list_in_progress_page_changed', {
      type_change: typeChange,
      page: newPage,
    });

    changePage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleClick(
    event: React.MouseEvent,
    journey: JourneysItemInterface,
  ) {
    const { tagName } = event.target as HTMLElement;

    if (
      tagName !== 'FUEL-BUTTON' &&
      tagName !== 'BUTTON' &&
      tagName !== 'FUEL-ICON' &&
      tagName !== 'FUEL-BADGE' &&
      tagName !== 'FUEL-INTERACTIVE-ICON'
    ) {
      dispatch({
        type: 'ui_modalMonitoring_set_show',
        payload: {
          isShow: true,
          flow: 'list-item',
          journeyId: journey.id,
        },
      });
    }
  }

  function handlerModalDialogue(
    props: JourneysItemInterface,
    flow?: 'markerMap' | 'button',
  ) {
    const { address } = props;
    handlerOpenModalConfirm(props);
    amplitudeEvent('journey_finish_clicked', {
      source:
        flow === 'markerMap'
          ? 'finalizar-viagem-mapa-viagem'
          : 'button-finalizar-viagem-lista-item',
      journey_id: props.id,
      pickup: address.pickup.description || '',
      delivery: address.delivery.description || '',
    });
  }

  function handleOpenEvaluating(journeyId: string) {
    if (!evaluatingStatus?.company_evaluating?.evaluated) {
      openCompanyEvaluating(journeyId);
      amplitudeEvent('company_evaluating_journey', {
        source: 'confirmacao-entrega-avaliar-motorista',
      });
    }
  }

  function handleJourneys() {
    fetchJourneys({
      page: 1,
    });
  }

  function handleDialogUnrecognized(journeyId: string) {
    amplitudeEvent('journey_unrecognized_clicked', {
      source: 'kebab-viagem',
      journey_id: journeyId,
    });

    dispatch({
      type: 'ui_dialogUnrecognized_set_show',
      payload: { isShow: true, journeyId },
    });
  }

  function handleDialogCancelJourney(
    journeyId: string,
    depositFee: JourneysItemInterface['trucker']['depositFee'],
  ) {
    dispatch({
      type: 'ui_dialogCancelJourney_set_show',
      payload: { isShow: true, journeyId, depositFee },
    });
  }

  function handleClickActionMarker(
    type: ActionClickMarkerType,
    journeyId: string,
  ) {
    const journeyState = getJourney(journeySelected, journeyId);

    if (journeyState) {
      if (type === 'confirmJourney') {
        handlerModalDialogue(journeyState, 'markerMap');
        amplitudeEvent('journey_confirm_delivery_clicked', {
          source: 'popup-map-viagem',
          journey_id: journeyId,
        });
        return;
      }

      dispatch({
        type: 'ui_modalUpdatedAddress_set_show',
        payload: {
          isShow: true,
          flow: 'markerMap',
          journeyId,
        },
      });

      amplitudeEvent('journey_add_route_clicked', {
        source: 'popup-map-viagem',
        journey_id: journeyId,
      });
      return;
    }

    throw new Error('journeyState not found');
  }

  function sentWhatsamplitudeEvent(journey: JourneysItemInterface) {
    amplitudeEvent('journey_whatsapp_for_trucker_sent', {
      source: 'item-lista-viagem',
      journey_id: journey.id,
    });
  }

  function handleDispatchTogglePopupMarker(dataEvent: CustomEvent) {
    const journeyId = dataEvent.detail as string;

    dispatch({
      type: 'journey_toggle_selection_marker',
      payload: { journeyId },
    });

    amplitudeEvent('journey_pin_clicked', {
      source: 'map-viagem',
      journey_id: journeyId,
    });
  }

  function handleDispatchClearPopupMarker() {
    dispatch({
      type: 'journey_clear_selection_marker',
    });
  }

  function handleUpdateJourneysAfterProposalConfirmeted() {
    handleJourneys();
  }

  function emptyJourneys() {
    if (
      !journeySelected.isFetching &&
      !journeySelected.error &&
      journeySelected.isFetched &&
      journeySelected.meta.total === 0 &&
      !proposals.pending.isFetching &&
      !proposals.pending.error &&
      proposals.pending.isFetched &&
      proposals.pending.meta.total === 0 &&
      !filters.status.length
    ) {
      return true;
    }

    return false;
  }

  const allJourneyTabsEmpty = emptyJourneys();

  useEffect(() => {
    if (!allJourneyTabsEmpty) {
      window.addEventListener('togglePopupMarker', (dataEvent) =>
        handleDispatchTogglePopupMarker(dataEvent as CustomEvent),
      );

      window.addEventListener('clickMapInner', () =>
        handleDispatchClearPopupMarker(),
      );

      window.addEventListener('closePopupMarker', () =>
        handleDispatchClearPopupMarker(),
      );

      window.addEventListener(
        'proposalConfirmedSuccess',
        handleUpdateJourneysAfterProposalConfirmeted,
      );
    }

    return () => {
      window.removeEventListener('togglePopupMarker', (dataEvent) =>
        handleDispatchTogglePopupMarker(dataEvent as CustomEvent),
      );

      window.removeEventListener('clickMapInner', () =>
        handleDispatchClearPopupMarker(),
      );

      window.removeEventListener('closePopupMarker', () =>
        handleDispatchClearPopupMarker(),
      );

      window.removeEventListener(
        'proposalConfirmedSuccess',
        handleUpdateJourneysAfterProposalConfirmeted,
      );
    };
  }, []);

  useEffect(() => {
    if (
      monitoringJourneyId &&
      journeySelected?.isFetched &&
      journeySelected?.data
    ) {
      const journeySelectedInList = journeySelected?.data.find(
        (journey) => journey.id === monitoringJourneyId,
      );

      if (journeySelectedInList) {
        dispatch({
          type: 'ui_modalMonitoring_set_show',
          payload: {
            isShow: true,
            flow: 'list-item',
            journeyId: journeyIdDialogDeliveryConfirmation,
          },
        });
      }
    }
  }, [journeySelected?.isFetched]);

  useEffect(() => {
    if (journeySelected?.error && !errorCountRef.current) {
      amplitudeEvent('journey_list_service_error_viewed', {
        source: 'viagens-all',
        message_error: 'lista-não-carregada',
      });
    }
  }, [journeySelected?.error]);

  useEffect(() => {
    const canTrackView =
      !journeySelected?.error &&
      journeySelected?.isFetched &&
      !errorCountRef.current;

    if (canTrackView) {
      amplitudeEvent('journey_list_in_progress_viewed', {
        source: 'viagens-all',
        has_journey_created: journeySelected?.data?.length ? 'sim' : 'nao',
      });
    }

    if (journeySelected?.error && errorCountRef.current) {
      amplitudeEvent('journey_list_service_error_viewed', {
        source: 'tentar-novamente-lista-viagens',
        message_error: 'lista-não-carregada',
      });
      errorCountRef.current = 0;
    }

    if (
      !journeySelected?.error &&
      !journeySelected?.isFetching &&
      journeySelected?.isFetched &&
      errorCountRef.current
    ) {
      amplitudeEvent('journey_list_done_viewed', {
        source: 'tentar-novamente-lista-viagens',
      });

      errorCountRef.current = 0;
    }
  }, [journeySelected?.error, journeySelected?.isFetched]);

  if (allJourneyTabsEmpty) {
    return <JourneyOnboarding isMobile={isMobile} />;
  }

  return (
    <>
      <Styled.Wrapper>
        <JourneysAllTab
          isMobile={isMobile}
          handleClick={(event, journey) => handleClick(event, journey)}
          handlerModalDialogue={(journey) => handlerModalDialogue(journey)}
          onCallbackSentWhatsApp={(journey) => sentWhatsamplitudeEvent(journey)}
          onClickUnrecognized={(id) => handleDialogUnrecognized(id)}
          onClickCancelJourney={(id, depositFee) =>
            handleDialogCancelJourney(id, depositFee)
          }
          onErrorGetAgain={() => {
            errorCountRef.current += 1;
            onErrorGetAgain();
          }}
          onChangePage={(nextPage) => handlePageChange(nextPage)}
          onClickActionMarker={(type, journeyId) =>
            handleClickActionMarker(type, journeyId)
          }
        />
      </Styled.Wrapper>

      <Dialog
        title="Confirmação de entrega concluída"
        confirmText="Entrega concluída"
        isOpen={!isMobile && isShowDialogDeliveryConfirmation}
        onClose={() => {
          amplitudeEvent('journey_list_in_progress_viewed', {
            source: 'x-fechar-finalização-viagem',
            has_journey_created: journeySelected.data?.length ? 'sim' : 'nao',
          });
          handlercloseModalConfirm();
        }}
        refuseText="Cancelar"
        onConfirm={() => {
          submitJourneyFinish(
            journeyIdDialogDeliveryConfirmation,
            isShowModalMonitoring
              ? 'confirmar-entrega-drawer'
              : 'confirmar-finalizar-viagem',
          );
        }}
        onRefuse={() => {
          amplitudeEvent('journey_list_in_progress_viewed', {
            source: 'voltar-finalização-viagem',
            has_journey_created: journeySelected.data?.length ? 'sim' : 'nao',
          });
          handlercloseModalConfirm();
        }}
        width="xs"
        spacing={3}
        isLoading={isLoading}
      >
        <FuelTypography size="body-md" weight="medium" color="neutral-default">
          Ao confirmar a entrega, esta viagem será movida para aba ”Encerradas”
          e não ficará disponível para monitoramento.
        </FuelTypography>
      </Dialog>

      <FuelBottomSheet
        title="Confirmação de entrega concluída"
        confirmText="Entrega concluída"
        isOpen={isMobile && isShowDialogDeliveryConfirmation}
        onClose={() => {
          amplitudeEvent('journey_list_in_progress_viewed', {
            source: 'x-fechar-finalização-viagem',
            has_journey_created: journeySelected.data?.length ? 'sim' : 'nao',
          });
          handlercloseModalConfirm();
        }}
        refuseText="Cancelar"
        onConfirm={() => {
          submitJourneyFinish(
            journeyIdDialogDeliveryConfirmation,
            'confirmar-finalizar-viagem',
          );
        }}
        onRefuse={() => {
          amplitudeEvent('journey_list_in_progress_viewed', {
            source: 'voltar-finalização-viagem',
            has_journey_created: journeySelected.data?.length ? 'sim' : 'nao',
          });
          handlercloseModalConfirm();
        }}
        isLoading={isLoading}
        portalName="journey-web-confirmar-finalizar-viagem"
        height={295}
        zIndex={1000}
      >
        <FuelTypography
          size="body-md"
          weight="medium"
          color="neutral-default"
          spacingTop={16}
        >
          Ao confirmar a entrega, esta viagem será movida para aba ”Encerradas”
          e não ficará disponível para monitoramento.
        </FuelTypography>
      </FuelBottomSheet>
    </>
  );
}

export default JourneyList;
